<script setup lang="ts">
import {
  computed,
  ref,
  toRef,
} from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  IPaymentSystemsFields,
  IPaymentSystemWithdrawalSchema,
} from '@modules/payment-systems/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useConfig } from '@modules/config/composables';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePaymentSystems } from '@modules/payment-systems/composables';
import {
  PAYMENT_SYSTEMS,
  TRANSLATION_GROUP_PAYMENT_SYSTEMS,
} from '@modules/payment-systems/constants';
import { usePaymentSystemsWithdrawalMutation } from '@modules/payment-systems/mutations';
import { usePaymentSystemsAllowedQuery } from '@modules/payment-systems/queries';
import { transformNumber } from '@utils/numbers';

import type {
  PaymentSystemWithdrawalFormEmits,
  PaymentSystemWithdrawalFormProps,
} from './types';

const props = withDefaults(defineProps<PaymentSystemWithdrawalFormProps>(), {
  queryType: 'default',
});
const emit = defineEmits<PaymentSystemWithdrawalFormEmits>();

const affId = toRef(props, 'affId');
const queryType = toRef(props, 'queryType');
const withDefault = toRef(props, 'withDefault');
const permission = toRef(props, 'permission');

const { config } = useConfig();
const { getTranslation } = useI18n();
const {
  data: allowedPaymentSystems,
  isLoading,
} = usePaymentSystemsAllowedQuery(affId);

const {
  mutateAsync,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = usePaymentSystemsWithdrawalMutation(queryType, permission);

const allowedPaymentSystemIds = computed<string[]>(() => {
  const systems = [...(allowedPaymentSystems.value ?? [])];

  systems.sort((a, b) => {
    if (a.default === b.default) {
      return 0;
    }

    if (a.default && !b.default) {
      return -1;
    }

    return 1;
  });

  return systems.map((item) => item.payment_system_name);
});

const selectedPaymentSystemInner = ref<string>();
const selectedPaymentSystem = computed(() => (
  selectedPaymentSystemInner.value || allowedPaymentSystemIds.value?.[0]
));

const selectedPaymentSystemValues = computed<IPaymentSystemsFields>(() => (
  allowedPaymentSystems.value?.find((item) => item.payment_system_name === selectedPaymentSystem.value) ?? {}
));

const {
  paymentSystemSchema,
} = usePaymentSystems(
  selectedPaymentSystem,
  affId,
  withDefault,
  queryType,
  false,
  selectedPaymentSystemValues,
);

const paymentSystems = computed<IFormSelectInputOption<string>[]>(() => (
  PAYMENT_SYSTEMS.filter((item) => allowedPaymentSystemIds.value.includes(item.name)).map((item) => ({
    value: item.name,
    label: item.name,
  }))
));

const schema = computed<FormSchema<IPaymentSystemWithdrawalSchema, never, {
  country_code: {
    selectOptionValue: 'country_code';
    selectOptionLabel: 'name';
  };
}>>(() => ({
  payment_system_name: {
    type: props.isSelectButton ? EFormInputTypes.SELECT_BUTTON : EFormInputTypes.SELECT,
    options: paymentSystems.value,
    label: !props.isSelectButton ? getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'WITHDRAWAL_SELECT').value : undefined,
    value: selectedPaymentSystem.value,
    required: true,
  },
  ...paymentSystemSchema.value,
}));

function onFormChange<
  T extends IPaymentSystemWithdrawalSchema,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  if (key === 'payment_system_name') {
    selectedPaymentSystemInner.value = value as string;
  }
}

async function onSubmit(values: IPaymentSystemWithdrawalSchema): Promise<void> {
  await mutateAsync({
    ...selectedPaymentSystemValues.value,
    ...values,
    affiliate_id: props.affId,
  });

  emit('submit');
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'PaymentSystemWithdrawalForm'"
    min-height="200px"
    :loading="isLoading"
  >
    <div>
      <div
        v-if="balance !== undefined"
        class="mb-4 text-6xl font-medium"
      >
        {{ config?.currency }} {{ transformNumber(balance) }}
      </div>
      <VForm
        :schema="schema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'WITHDRAWAL_SUBMIT')"
        :custom-errors="errors"
        :is-loading="isLoadingSave"
        :readonly="readonly"
        @change="onFormChange"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VBackButton
            v-if="backRoute"
            :to="backRoute"
            :label="backLabel || $getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, 'WITHDRAWAL_BACK')"
          />
        </template>
      </VForm>
    </div>
  </VLoader>
</template>
